import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Grid, Paper, TablePaginationProps } from '@mui/material';

import { Table } from '../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../components/table/types';
import { useGetProviderLedgersQuery, useLazyGetProviderLedgersQuery } from '../../../api';
import { UTCToDateTime } from '../../../utils/dates';
import { formatAmount } from '../../../utils';
import { useParams } from 'react-router-dom';
import { TablePagination } from '../../../components';

export const Ledgers: FC = () => {
  const { t } = useTranslation();
  const { provider } = useParams<'provider'>();

  // api
  const [getProviderLedgers, { data: krakenInfo, isLoading: isKrakenInfoLoading }] =
    useLazyGetProviderLedgersQuery();

  // pagination logic
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const handleChangePage: TablePaginationProps['onPageChange'] = (e, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage: TablePaginationProps['onRowsPerPageChange'] = (e) => {
    setPage(0);
    setRowsPerPage(parseInt(e.target.value, 10));
  };

  useEffect(() => {
    if (provider) {
      const args = {
        provider,
        params: {
          page: 1,
          page_size: rowsPerPage,
        },
      };
      getProviderLedgers(args);
    }
  }, [provider]);

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('providers.head-cells-ledgers.refid') },
    { id: '2', label: t('providers.head-cells-ledgers.amount') },
    { id: '4', label: t('providers.head-cells-ledgers.asset') },
    { id: '5', label: t('providers.head-cells-ledgers.balance') },
    { id: '6', label: t('providers.head-cells-ledgers.fee') },
    { id: '7', label: t('providers.head-cells-ledgers.subtype') },
    { id: '8', label: t('providers.head-cells-ledgers.type') },
    { id: '9', label: t('providers.head-cells-ledgers.time') },
  ];

  const rows: Row[] | undefined = krakenInfo
    ? Object.values(krakenInfo.ledgers).map((ledger) => {
        const cells: RowCell[] = [
          {
            name: 'refid',
            value: ledger.refid,
          },
          {
            name: 'amount',
            value: formatAmount(ledger.amount),
          },
          {
            name: 'asset',
            value: ledger.asset,
          },
          {
            name: 'balance',
            value: formatAmount(ledger.balance),
          },
          {
            name: 'fee',
            value: formatAmount(ledger.fee),
          },
          {
            name: 'subtype',
            value: ledger.subtype,
          },
          {
            name: 'type',
            value: ledger.type,
          },
          {
            name: 'time',
            value: ledger.time,
            formatting: (value: number) => {
              return UTCToDateTime(new Date(value * 1000).toISOString());
            },
          },
        ];

        return { cells };
      })
    : [];

  return (
    <Grid size={{ xs: 12 }}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        <Table headCells={headCells} rows={rows} />
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          total={krakenInfo?.total ?? -1}
          totalPages={krakenInfo?.total_pages ?? 0}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          isLoading={isKrakenInfoLoading}
        />
      </Paper>
    </Grid>
  );
};
